import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import Grid from '../../webcommon/grid/Grid';
import PropTypes from 'prop-types';
import FilterHeader from '../../webcommon/header/filterheader/FilterHeader';
import moment from 'moment';
import { getFilters } from '../../webcommon/header/filterheader/selectors';
import ColumnSelector from '../../webcommon/columnselector/ColumnSelector';
import fp from 'lodash/fp';
import GridHeader from '../../common/gridheader/GridHeader';
import ToggleSwitch from '../../common/gridheader/toggleswitch/ToggleSwitch';
import styles from './CsaSummary.module.scss';
import {
    getColumns, getDateFrom, getCsas, getDateTo, getDefaultSort,
    getUseDollars
} from './selectors';
import {
    setCsaSummariesColumns, csaSummariesLoaded, refreshData, csvExportEmpty,
    setGridSorts, switchCurrencies, refreshCsaSummary
} from './actions';
import { PAGE_REFRESH_RATE } from './constants';
import { setDrilldownFilters } from '../csadrilldown/actions';
import gridStyle from './GridOverride.module.scss';
import ExportAsCsvButton from '../../common/gridheader/exportbutton/ExportAsCsvButton';
import { findGridSort } from '../../common/helpermethods/HelperMethods';
import * as appConstants from '../../common/constants';

const processDataForCsvExport = (rows) => {
    const mapDataKeys = (row) => {
        const {
            businessDate, csa, counterparty,
            imPledgorPaymentUsd, imPledgorPaymentEur, imPledgeUsd, imReceiveUsd, imPledgeEur, imReceiveEur, reconciliationStatus, comments,
            imThresholdPostUsd, imThresholdPostEur, imThresholdCollectEur, imThresholdCollectUsd
        } = row;
        return {
            EODDate: moment(Date.parse(businessDate)).format(appConstants.dateFormat),
            CSA: csa,
            Counterparty: counterparty,
            'IM Threshold Post USD': imThresholdPostUsd,
            'IM Threshold Post EUR': imThresholdPostEur,
            'IM Threshold Collect USD': imThresholdCollectUsd,
            'IM Threshold Collect EUR': imThresholdCollectEur,
            'Real IM Pledge USD': imPledgorPaymentUsd,
            'Real IM Pledge EUR': imPledgorPaymentEur,
            'IM Pledge USD': imPledgeUsd,
            'IM Receive USD': imReceiveUsd,
            'IM Pledge EUR': imPledgeEur,
            'IM Receive EUR': imReceiveEur,
            'Reconciliation Status': reconciliationStatus,
            Comments: comments
        };
    };
    return rows.map(mapDataKeys);
};

const calculateCsvFilename = (dateFrom, dateTo, csas) => {
    const title = 'IMCSASummary';
    const filtered = csas.length ? 'filtered' : '';
    if (dateFrom && dateTo) {
        const startDate = moment(dateFrom).format(appConstants.dateFormat);
        const endDate = moment(dateTo).format(appConstants.dateFormat);
        return fp.compact([title, filtered, startDate, endDate]).join('_').concat('.csv');
    }
    const dateToday = moment().format(appConstants.dateFormat);
    return fp.compact([title, filtered, dateToday]).join('_').concat('.csv');
};

const removeBusinessDate = (dateFrom, dateTo) => columns => ((dateFrom === null || dateTo === null)
    ? columns.filter(col => col.id !== 'businessDate') : columns);

const filterColumns = (dateFrom, dateTo, columns) => fp.flow(
    columns => columns.filter(col => col.visible),
    removeBusinessDate(dateFrom, dateTo)
)(columns);

const CsaSummary = (props) => {
    const {
        csaSummariesLoaded,
        useDollars,
        columns,
        dateFrom,
        dateTo,
        refreshCsaSummary
    } = props;


    useEffect(() => {
        csaSummariesLoaded();
        const continuousDataLoad = setInterval(() => {
            refreshCsaSummary();
        }, PAGE_REFRESH_RATE);
        return () => clearInterval(continuousDataLoad);
    }, [csaSummariesLoaded, refreshCsaSummary]);

    const currentCurrency = useMemo(() => (useDollars ? 'USD' : 'EUR'), [useDollars]);

    const columnsWithSymbols = useMemo(() => columns.map(
        c => (c.hasSymbol
            ? fp.set('symbol', currentCurrency)(c)
            : c)
    ), [columns, currentCurrency]);

    const setCsaSummaryColumns = (columns) => {
        const data = columns.map((column) => {
            const { id, visible } = column;
            return { id, visible };
        });
        props.setCsaSummariesColumns(data);
    };

    const setSorting = (newSorted) => {
        const idWithoutCurrency = fp.flow(
            fp.find(column => column.id === newSorted[0].id),
            fp.get('idWithoutCurrency')
        )(props.columns);
        const sortedWithStrippedId = idWithoutCurrency ? fp.set('[0].id', idWithoutCurrency)(newSorted) : newSorted;
        props.setGridSorts('csaSummary', sortedWithStrippedId);
    };

    const setGridSorts = useMemo(() => {
        const gridSorts = findGridSort(props.defaultSort, 'csaSummary');
        return gridSorts.map((columnSort) => {
            const currencyId = fp.flow(
                fp.find(column => column.idWithoutCurrency === columnSort.id),
                fp.get('id')
            )(props.columns);
            return currencyId ? fp.set('id', currencyId)(columnSort) : columnSort;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.defaultSort, props.useDollars, props.columns]);

    const memoizedColumns = useMemo(() => filterColumns(dateFrom,
        dateTo, columns), [dateFrom, dateTo, columns]);

    return (
        <div className={ styles.csaSummary }>
            <div className={ styles.headerWrapper }>
                <FilterHeader
                    title="Summary"
                    id="CSASummary"
                />
                <div className={ styles.currencySelection }>
                    <ToggleSwitch
                        optionLeft="EUR"
                        optionRight="USD"
                        toggleIsLeft={ !props.useDollars }
                        toggleSwitch={ props.switchCurrencies }
                    />
                </div>
            </div>
            <div className={ styles.gridHeaderContainer }>
                <GridHeader
                    lastUpdated={ props.lastUpdated }
                    refreshControlsActive
                    refreshData={ props.refreshData }
                >
                    <ColumnSelector
                        columns={ columnsWithSymbols }
                        applyFilter={ setCsaSummaryColumns }
                    />
                    <div className={ styles.separator }>&nbsp;</div>
                    <ExportAsCsvButton
                        csvData={ props.rows }
                        csvFilename={
                            calculateCsvFilename(props.dateFrom, props.dateTo, props.csas)
                        }
                        csvExportEmpty={ props.csvExportEmpty }
                        csvMapping={ processDataForCsvExport }
                    />
                </GridHeader>
            </div>
            <div className={ styles.gridContainer }>
                {props.columns.length ? (
                    <Grid
                        columns={ memoizedColumns }
                        defaultColumnSpacing={ 2 }
                        defaultSorted={ setGridSorts }
                        loading={ props.loading }
                        onRowClick={ row => props.setDrilldownFilters(
                            row.csa, row.businessDate, row.counterparty
                        ) }
                        onSortedChange={ setSorting }
                        rows={ props.rows }
                        styles={ gridStyle }
                        symbol={ currentCurrency }
                    >
                        CSA Summary area
                    </Grid>
                ) : null}
            </div>
        </div>
    );
};

CsaSummary.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            id: PropTypes.string,
            type: PropTypes.string
        })
    ),
    csas: PropTypes.arrayOf(PropTypes.string),
    csvExportEmpty: PropTypes.func.isRequired,
    csaSummariesLoaded: PropTypes.func.isRequired,
    dateFrom: PropTypes.string,
    dateTo: PropTypes.string,
    defaultSort: PropTypes.arrayOf(
        PropTypes.shape({
            gridId: PropTypes.string,
            gridSort: PropTypes.arrayOf(PropTypes.shape({
                id: PropTypes.string,
                desc: PropTypes.bool
            }))
        })
    ),
    lastUpdated: PropTypes.number,
    loading: PropTypes.bool,
    refreshData: PropTypes.func.isRequired,
    rows: PropTypes.arrayOf(PropTypes.shape({})),
    setCsaSummariesColumns: PropTypes.func.isRequired,
    setDrilldownFilters: PropTypes.func.isRequired,
    setGridSorts: PropTypes.func.isRequired,
    location: PropTypes.shape({
        search: PropTypes.string
    }).isRequired,
    useDollars: PropTypes.bool.isRequired,
    switchCurrencies: PropTypes.func.isRequired
};

CsaSummary.defaultProps = {
    columns: [],
    csas: [],
    dateFrom: '',
    defaultSort: [],
    dateTo: '',
    loading: false,
    rows: [],
    lastUpdated: moment().unix()
};

const mapStateToProps = state => ({
    columns: getColumns(state),
    csas: getCsas(getFilters(state, 'CSASummary')),
    dateFrom: getDateFrom(getFilters(state, 'CSASummary')),
    dateTo: getDateTo(getFilters(state, 'CSASummary')),
    defaultSort: getDefaultSort(state),
    lastUpdated: state.csaSummary.lastUpdated,
    loading: state.csaSummary.loading,
    rows: state.csaSummary.csaSummaries,
    useDollars: getUseDollars(state)
});

const mapDispatchToProps = dispatch => ({
    setCsaSummariesColumns: data => dispatch(setCsaSummariesColumns(data)),
    setDrilldownFilters: (csa, date, counterparty) => dispatch(
        setDrilldownFilters(csa, date, counterparty)
    ),
    csaSummariesLoaded: () => dispatch(csaSummariesLoaded()),
    csvExportEmpty: error => dispatch(csvExportEmpty(error)),
    refreshData: () => dispatch(refreshData()),
    setGridSorts: (gridId, sorting) => dispatch(setGridSorts(gridId, sorting)),
    switchCurrencies: () => dispatch(switchCurrencies()),
    refreshCsaSummary: () => dispatch(refreshCsaSummary())
});

export default withRouter(connect(
    mapStateToProps,
    mapDispatchToProps
)(CsaSummary));

export { CsaSummary as CsaSummaryUnconnected };
